/**
 * Deep link бота и Mini App (BLUEPRINT 3.9): `startapp=ref_<code>` / `seat_<squadId>` /
 * `reserve_<token>` / `pay_<orderId>` / `g_<gatheringId>`. Разбирается одинаково ботом
 * (`/start <payload>`) и приложением (`tgWebAppStartParam`).
 */
export type DeepLink =
  | { kind: 'ref'; code: string }
  | { kind: 'seat'; squadId: string }
  | { kind: 'reserve'; token: string }
  | { kind: 'pay'; orderId: string }
  | { kind: 'gathering'; gatheringId: string };

/** Telegram допускает в start-параметре только `A-Za-z0-9_-`, до 64 символов. */
const START_PARAM_RE = /^[A-Za-z0-9_-]{1,64}$/;
const VALUE_RE = /^[A-Za-z0-9_-]{1,60}$/;

export function isValidStartParam(raw: string): boolean {
  return START_PARAM_RE.test(raw);
}

export function parseStartParam(raw: string | null | undefined): DeepLink | null {
  if (!raw || !isValidStartParam(raw)) return null;
  const idx = raw.indexOf('_');
  if (idx <= 0) return null;
  const prefix = raw.slice(0, idx);
  const value = raw.slice(idx + 1);
  if (!VALUE_RE.test(value)) return null;
  switch (prefix) {
    case 'ref':
      return { kind: 'ref', code: value };
    case 'seat':
      return { kind: 'seat', squadId: value };
    case 'reserve':
      return { kind: 'reserve', token: value };
    case 'pay':
      return { kind: 'pay', orderId: value };
    case 'g':
      return { kind: 'gathering', gatheringId: value };
    default:
      return null;
  }
}

export function formatStartParam(link: DeepLink): string {
  switch (link.kind) {
    case 'ref':
      return `ref_${link.code}`;
    case 'seat':
      return `seat_${link.squadId}`;
    case 'reserve':
      return `reserve_${link.token}`;
    case 'pay':
      return `pay_${link.orderId}`;
    case 'gathering':
      return `g_${link.gatheringId}`;
  }
}

/**
 * Путь Mini App, на который открывается ссылка (карта экранов 3.8).
 * Реферальная ссылка ведёт на `/`: код обрабатывается отдельно (`referral.attach`), а экран
 * выбирается по состоянию пользователя.
 */
export function pathForDeepLink(link: DeepLink): string {
  switch (link.kind) {
    case 'ref':
      return '/';
    case 'seat':
      return `/open-seats?squad=${encodeURIComponent(link.squadId)}`;
    case 'reserve':
      return `/squad/reserve?token=${encodeURIComponent(link.token)}`;
    case 'pay':
      return `/pay/result/${encodeURIComponent(link.orderId)}`;
    case 'gathering':
      return `/gathering/${encodeURIComponent(link.gatheringId)}`;
  }
}

/**
 * Безопасный относительный путь для `?path=` при открытии через кнопку бота:
 * только абсолютный путь внутри приложения, без схемы и хоста (`//evil`), без обратных слэшей.
 */
export function sanitizeAppPath(raw: string | null | undefined): string | null {
  if (!raw) return null;
  if (!raw.startsWith('/') || raw.startsWith('//') || raw.includes('\\')) return null;
  if (/[\s<>]/.test(raw) || raw.length > 200) return null;
  return raw;
}

/** Ссылка на бота с deep link: `https://t.me/<bot>?startapp=<param>`. */
export function botStartAppUrl(botUsername: string, link: DeepLink): string {
  return `https://t.me/${botUsername}?startapp=${formatStartParam(link)}`;
}

/** Ссылка `/start <param>` (открывает чат с ботом): `https://t.me/<bot>?start=<param>`. */
export function botStartUrl(botUsername: string, link: DeepLink): string {
  return `https://t.me/${botUsername}?start=${formatStartParam(link)}`;
}

/**
 * Привязка группы отряда кнопкой (раунд 4, C1). Ссылка `startgroup` открывает в Telegram выбор
 * группы; `admin=invite_users` сразу просит у капитана единственное право, которое нужно боту, —
 * выгрузить ссылку-приглашение (`exportChatInviteLink`): сообщения и опросы бот шлёт как обычный
 * участник. Добавив бота, Telegram отправляет в группу `/start bind_<squadId>` от имени капитана —
 * бот привязывает группу тем же путём, что и `/bind`, с той же проверкой прав.
 */
const GROUP_BIND_PREFIX = 'bind_';

export function botAddToGroupUrl(botUsername: string, squadId: string): string {
  return `https://t.me/${botUsername}?startgroup=${GROUP_BIND_PREFIX}${squadId}&admin=invite_users`;
}

/** `bind_<squadId>` из `/start` в группе → id отряда; иначе null. */
export function parseGroupBindParam(payload: string | null | undefined): string | null {
  if (!payload?.startsWith(GROUP_BIND_PREFIX)) return null;
  const id = payload.slice(GROUP_BIND_PREFIX.length);
  return /^[A-Za-z0-9_-]{1,60}$/.test(id) ? id : null;
}
